import { BasicButton } from 'src/design/atoms/BasicButton';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import { DatesRangeValue } from '@mantine/dates';
import { useDebouncedCallback } from '@mantine/hooks';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { AdminFeedbackItemDto, UserDto } from 'src/api/FiretrolApiClient';
import { useUsersList } from 'src/api/queries/users/useUsersList';
import {
  AdminFeedbackFilters,
  useAdminFeedback,
} from 'src/api/queries/chatAdmin/useAdminFeedback';
import { UserSelect } from 'src/app/selectors/UserSelect';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { FeedbackRating } from 'src/routes/Settings/AIAssistant/aiAssistantSearchParams';
import { FeedbackFacetChips } from 'src/routes/Settings/AIAssistant/components/FeedbackFacetChips';
import { getFeedbackColumns } from 'src/routes/Settings/AIAssistant/components/feedbackColumns';
import { DateRangeFilter } from 'src/routes/Settings/AIAssistant/components/DateRangeFilter';
import { ConversationDetailModal } from 'src/routes/Settings/AIAssistant/ConversationDetailModal';
import { FEEDBACK_EXPORT_MAX_ROWS } from 'src/routes/Settings/AIAssistant/constants';
import { useFeedbackExport } from 'src/routes/Settings/AIAssistant/hooks/useFeedbackExport';
import { useFeedbackFilters } from 'src/routes/Settings/AIAssistant/hooks/useFeedbackFilters';
import { formatDate, formatNumber } from 'src/utils';

const EMPTY_ITEMS: AdminFeedbackItemDto[] = [];
const EMPTY_USERS: UserDto[] = [];
const DAY_KEY_FORMAT = 'YYYY-MM-DD';

const toDayKey = (date: Date | null) => formatDate(date, DAY_KEY_FORMAT);

const fromDayKey = (key: string) =>
  key ? moment(key, DAY_KEY_FORMAT).toDate() : undefined;

interface ISelectedVote {
  conversationId: string;
  messageId: string;
}

export const FeedbackTab: React.FC = () => {
  const filterState = useFeedbackFilters();
  const { dateRange, rating, reasons, userId } = filterState;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedVote, setSelectedVote] = useState<ISelectedVote | null>(null);
  const { data: users = EMPTY_USERS } = useUsersList();
  const { exportCsv, exporting } = useFeedbackExport();

  // The URL is re-read on every search-param change (a tab switch included),
  // which rebuilds the Date objects; keying on the calendar day keeps the
  // filters, and so the query key and page, stable across those.
  const fromKey = toDayKey(dateRange[0]);
  const toKey = toDayKey(dateRange[1]);
  const reasonsKey = reasons.join(',');

  const filters: AdminFeedbackFilters = useMemo(
    () => ({
      userId,
      from: fromDayKey(fromKey),
      to: fromDayKey(toKey),
      search: debouncedSearch || undefined,
      rating,
      reasons: reasonsKey ? reasonsKey.split(',') : undefined,
    }),
    [userId, fromKey, toKey, debouncedSearch, rating, reasonsKey],
  );

  const openVote = useCallback(
    (item: AdminFeedbackItemDto) =>
      setSelectedVote({
        conversationId: item.conversationId,
        messageId: item.messageId,
      }),
    [],
  );
  const columns = useMemo(() => getFeedbackColumns(openVote), [openVote]);
  const tableState = useTableState({
    identifier: 'ai-assistant-feedback',
    columns,
  });
  const { pagination, setPagination } = tableState;

  // Resetting in the same event as the filter change batches both updates
  // into one render, so the stale page is never requested for the new filters.
  const resetPage = () =>
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  const withPageReset =
    <Args extends unknown[]>(change: (...args: Args) => void) =>
    (...args: Args) => {
      change(...args);
      resetPage();
    };

  const applySearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value);
    resetPage();
  }, 300);

  const feedbackQuery = useAdminFeedback({
    ...filters,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  });
  const items = feedbackQuery.data?.items ?? EMPTY_ITEMS;
  const totalCount = feedbackQuery.data?.totalCount ?? 0;

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      enableSorting: false,
      enableColumnFilters: false,
      columns,
      data: items,
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      mantineTableBodyRowProps: ({ row }) => ({
        onClick: () => openVote(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    getRowId: (row) => row.messageId,
    totalCount,
    isLoading: feedbackQuery.isLoading,
    isFetching: feedbackQuery.isFetching,
    isError: feedbackQuery.isError,
    onRefresh: feedbackQuery.refetch,
  });

  return (
    <Stack gap="md">
      <Group align="flex-end" gap="md" wrap="wrap">
        <DateRangeFilter
          value={dateRange}
          onChange={withPageReset((value: DatesRangeValue) =>
            filterState.setDateRange(value),
          )}
        />
        <Box w={260}>
          <UserSelect
            value={users.find((user) => user.id === userId) ?? null}
            onChange={withPageReset((user: UserDto | null) =>
              filterState.setUserId(user?.id),
            )}
          />
        </Box>
        <TextInput
          label="Search"
          placeholder="Comment, question, or user"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            applySearch(e.target.value);
          }}
          w={260}
        />
        <Group gap="sm" wrap="nowrap" ml="auto">
          {totalCount > FEEDBACK_EXPORT_MAX_ROWS && (
            <Text size="xs" c="gray.7" maw={220} ta="right">
              Export includes the newest{' '}
              {formatNumber(FEEDBACK_EXPORT_MAX_ROWS)} of{' '}
              {formatNumber(totalCount)} votes
            </Text>
          )}
          <BasicButton
            variant="default"
            leftSection={<IconDownload size={16} />}
            loading={exporting}
            onClick={() => exportCsv(filters)}
          >
            Export CSV
          </BasicButton>
        </Group>
      </Group>
      <FeedbackFacetChips
        upCount={feedbackQuery.data?.upCount ?? 0}
        downCount={feedbackQuery.data?.downCount ?? 0}
        reasonCounts={feedbackQuery.data?.reasonCounts ?? []}
        rating={rating}
        reasons={reasons}
        onRatingToggle={withPageReset((value: FeedbackRating) =>
          filterState.toggleRating(value),
        )}
        onReasonToggle={withPageReset((reason: string) =>
          filterState.toggleReason(reason),
        )}
      />
      {table}
      <ConversationDetailModal
        conversationId={selectedVote?.conversationId ?? null}
        focusMessageId={selectedVote?.messageId}
        onClose={() => setSelectedVote(null)}
      />
    </Stack>
  );
};
